import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext/AuthContext'

import { useAuthenticatedMutation } from './useAuth'
import { CREATE_USER_VEHICLE } from '../graphql/mutation/createUserVehicle.mutation'
import { UPDATE_USER_VEHICLE } from '../graphql/mutation/updateUserVehicle.mutation'
import { DELETE_USER_VEHICLE } from '../graphql/mutation/deleteUserVehicle.mutation'
import { CREATE_USER_LICENSE_PLATE } from '../graphql/mutation/createUserLicensePlate.mutation'
import { Vehicle } from '../types/vehicle.interface'
import { UPDATE_USER_LICENSE_PLATE } from '../graphql/mutation/updateUserLicensePlate.mutation'

export enum ErrorList {
    NO_USER = 'no_user',
    MISSING_FIELDS = 'missing_fields',
    DUPLICATE_PLATE = 'duplicate_plate',
    UNKNOWN_ERROR = 'unknown_error'
}

export const useVehicle = () => {
    const { user, setUser } = useAuth()
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)

    const [saveVehicle] = useAuthenticatedMutation({
        mutation: CREATE_USER_VEHICLE
    })

    const [saveLicensePlate] = useAuthenticatedMutation({
        mutation: CREATE_USER_LICENSE_PLATE
    })

    const [updateExistingVehicle] = useAuthenticatedMutation({
        mutation: UPDATE_USER_VEHICLE
    })

    const [updateLicensePlate] = useAuthenticatedMutation({
        mutation: UPDATE_USER_LICENSE_PLATE
    })

    const [deleteVehicle] = useAuthenticatedMutation({
        mutation: DELETE_USER_VEHICLE
    })

    const createVehicle = async (
        vehicle: Vehicle
    ): Promise<{ success: boolean; errorCode?: string; vehicle: Vehicle }> => {
        if (!user)
            return {
                success: false,
                errorCode: ErrorList.NO_USER,
                vehicle
            }

        const { plate, country, name, vehicle_type } = vehicle
        if (!plate || !country || !vehicle_type) {
            return {
                success: false,
                errorCode: ErrorList.MISSING_FIELDS,
                vehicle
            }
        }

        // check if the user already has the license plate
        const existingPlate = user.license_plates.find((p: any) => p.plate === plate && p.country === country)
        if (existingPlate) {
            return {
                success: false,
                errorCode: ErrorList.DUPLICATE_PLATE,
                vehicle
            }
        }

        try {
            const savedPlate = await saveLicensePlate({
                variables: {
                    request: {
                        plate,
                        country
                    }
                }
            })

            const plate_id = savedPlate?.data?.create_user_license_plate?.id || ''
            if (!plate_id) throw new Error('Failed to save license plate')

            const nameValue = name || `${t('profile.vehicle.vehicle')} ${(user?.vehicles?.length ?? 0) + 1}`

            setLoading(true)
            const savedVehicle = await saveVehicle({
                variables: {
                    request: {
                        name: nameValue,
                        manufacturer: '',
                        model: '',
                        plate_id,
                        vehicle_type
                    }
                }
            })

            const vehicleId = savedVehicle?.data?.create_user_vehicle?.id || ''
            vehicle.id = vehicleId
            vehicle.plate_id = plate_id

            // Update user state with the new vehicle
            setUser({
                ...user,
                vehicles: [
                    ...user.vehicles,
                    {
                        id: vehicleId,
                        name: nameValue,
                        manufacturer: '',
                        model: '',
                        plate_id,
                        vehicle_type
                    }
                ],
                license_plates: [
                    ...user.license_plates,
                    {
                        id: plate_id,
                        plate,
                        country
                    }
                ]
            })

            return {
                success: true,
                vehicle
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }

        return {
            success: false,
            errorCode: ErrorList.UNKNOWN_ERROR,
            vehicle
        }
    }

    const updateVehicle = async (
        vehicle: Vehicle
    ): Promise<{ success: boolean; errorCode?: string; vehicle: Vehicle }> => {
        if (!user)
            return {
                success: false,
                errorCode: ErrorList.NO_USER,
                vehicle
            }

        const { id, name, vehicle_type, plate_id, plate, country } = vehicle
        if (!id || !name || !vehicle_type)
            return {
                success: false,
                errorCode: ErrorList.MISSING_FIELDS,
                vehicle
            }

        // check if the user already has the license plate
        const existingPlate = user.license_plates.find((p: any) => p.plate === plate && p.country === country)
        if (existingPlate) {
            return {
                success: false,
                errorCode: ErrorList.DUPLICATE_PLATE,
                vehicle
            }
        }

        setLoading(true)
        try {
            const savedVehicle = await updateExistingVehicle({
                variables: {
                    request: {
                        id,
                        name,
                        manufacturer: '',
                        model: '',
                        vehicle_type: vehicle_type.toUpperCase()
                    }
                }
            })

            const newVehicle = savedVehicle?.data?.update_user_vehicle
            if (newVehicle.success) {
                setUser((user: any) => ({
                    ...user,
                    vehicles: user.vehicles.map((v: Vehicle) => {
                        if (v.id === id) {
                            return vehicle
                        }
                        return v
                    })
                }))
            }

            if (
                plate !== user.license_plates.find((p: any) => p.id === plate_id)?.plate ||
                country !== user.license_plates.find((p: any) => p.id === plate_id)?.country
            ) {
                const savedPlate = await updateLicensePlate({
                    variables: {
                        request: {
                            id: plate_id,
                            plate,
                            country
                        }
                    }
                })

                if (savedPlate?.data?.update_user_license_plate?.success) {
                    setUser((user: any) => ({
                        ...user,
                        license_plates: user.license_plates.map((p: any) => {
                            if (p.id === plate_id) {
                                return {
                                    ...p,
                                    plate,
                                    country
                                }
                            }
                            return p
                        })
                    }))
                }
            }

            setLoading(false)
            return {
                success: true,
                vehicle
            }
        } catch (error) {
            setLoading(false)
            console.error(error)
            return {
                success: false,
                errorCode: ErrorList.UNKNOWN_ERROR,
                vehicle
            }
        }
    }

    const removeVehicle = async (
        vehicleId: string,
        plateId: string
    ): Promise<{
        success: boolean
        error?: any
    }> => {
        setLoading(true)
        try {
            await deleteVehicle({
                variables: {
                    request: {
                        vehicle_id: vehicleId,
                        plate_id: plateId
                    }
                }
            })
            const vehicles = user?.vehicles.filter((vehicle) => vehicle.id !== vehicleId) || []
            const license_plates = user?.license_plates.filter((plate: any) => plate.id !== plateId) || []
            // Update the user state by removing the deleted vehicle
            setUser((prevUser: any) => {
                return {
                    ...prevUser,
                    vehicles,
                    license_plates
                }
            })
            return {
                success: true
            }
        } catch (error) {
            console.error('Failed to delete vehicle:', error)
            return {
                success: false,
                error
            }
        } finally {
            setLoading(false)
        }
    }

    return {
        loading,
        createVehicle,
        removeVehicle,
        updateVehicle
    }
}
